import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { LazyLoadImage } from 'react-lazy-load-image-component';

interface HeroData {
    id: string;
    title: string;
    img_desktop: string;
    img_mobile: string;
}

const API = process.env.REACT_APP_API_ENDPOINT;

export default function Hero() {
    const [slides, setSlides] = useState<HeroData[]>([]);
    const [current, setCurrent] = useState(0);
    const [loading, setLoading] = useState(true);
    const timerRef = useRef<ReturnType<typeof setInterval>>();
    const touchX = useRef(0);

    useEffect(() => {
        axios.get(`${API}/hero`)
            .then(r => setSlides(r.data.data ?? []))
            .catch(() => setSlides([]))
            .finally(() => setLoading(false));
    }, []);

    const imgSrc = (s: HeroData) =>
        `${API}/hero/getimg/${window.innerWidth <= 1024 ? s.img_mobile : s.img_desktop}`;

    const goTo = (i: number) => {
        clearInterval(timerRef.current);
        setCurrent((i + slides.length) % slides.length);
    };

    useEffect(() => {
        if (slides.length <= 1) return;
        timerRef.current = setInterval(() =>
            setCurrent(c => (c + 1) % slides.length), 5000);
        return () => clearInterval(timerRef.current);
    }, [slides.length, current]);

    if (loading) return (
        <>
            <div className="relative w-full h-[220px] sm:h-[300px] md:h-[450px] lg:h-[600px] overflow-hidden bg-slate-200">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-300 animate-pulse" />
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />

                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
                    <div className="h-2 w-7 rounded-full bg-slate-300 animate-pulse" />
                    <div className="h-2 w-2 rounded-full bg-slate-300 animate-pulse delay-75" />
                    <div className="h-2 w-2 rounded-full bg-slate-300 animate-pulse delay-150" />
                </div>
            </div>
            <style>{`@keyframes shimmer{to{transform:translateX(200%)}}`}</style>
        </>
    );

    if (!slides.length) return (
        <div className="w-full h-[768px] bg-slate-100 flex items-center justify-center text-slate-400 text-sm tracking-widest uppercase">
            No content
        </div>
    );

    return (
        <>
            <div
                className="relative w-full h-[768px] overflow-hidden"
                onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
                onTouchEnd={e => {
                    const d = touchX.current - e.changedTouches[0].clientX;
                    if (Math.abs(d) > 50) goTo(current + (d > 0 ? 1 : -1));
                }}
            >
                {slides.map((s, i) => (
                    <div
                        key={s.id}
                        className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                        style={{ opacity: i === current ? 1 : 0 }}
                    >
                        <LazyLoadImage
                            src={imgSrc(s)}
                            alt="offers"
                            effect="blur"
                            className="w-full h-full"
                            wrapperProps={{
                                style: { transitionDelay: '1s' },
                            }}
                        />
                        <div className="absolute inset-0 " />
                    </div>
                ))}

                {slides.length > 1 && (['left', 'right'] as const).map(side => (
                    <button
                        key={side}
                        onClick={() => goTo(current + (side === 'left' ? -1 : 1))}
                        aria-label={side}
                        className="absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full
                                   flex items-center justify-center backdrop-blur-sm
                                   bg-bprDarkBlue hover:bg-white/25 border border-white/20
                                   transition-colors duration-200"
                        style={{ [side]: 16 }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d={side === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
                        </svg>
                    </button>
                ))}

                {slides.length > 1 && (
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10 bg-black/30 backdrop-blur-sm px-3 py-2 rounded-full">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => goTo(i)}
                                aria-label={`Slide ${i + 1}`}
                                className="h-2 rounded-full transition-all duration-300 ease-out"
                                style={{
                                    width: i === current ? 24 : 8,
                                    backgroundColor: i === current ? '#2563EB' : 'rgba(255,255,255,0.6)',
                                }}
                            />
                        ))}
                    </div>
                )}

                {slides.length > 1 && (
                    <div className="absolute bottom-0 inset-x-0 h-[2px] bg-white/10 z-10">
                        <div
                            key={current}
                            className="h-full bg-bprDarkBlue"
                            style={{ animation: 'progress 5s linear forwards' }}
                        />
                    </div>
                )}
            </div>

            <style>{`
                @keyframes progress { from { width: 0 } to { width: 100% } }
            `}</style>
        </>
    );
}